import { ScheduleBasedTherapyRxFrequencyDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from 'react';
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { calculateTherapyRxFrequency } from "../../helpers/scheduleBasedStatsHelpers";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

interface TherapyRxFrequencyData {
  locationId: string,
  frequency: number,
  count: number,
  percentage: number,
}

const useTherapyRxFrequencyStats = (startDate: Date, endDate: Date, locationIds?: string[]) => {
  const BIStore = useStore(BIContext); 
  const { getTherapyRxFrequencyStats } = BIStore; 

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allTherapyRxFrequency, setAllTherapyRxFrequency] = useState<ScheduleBasedTherapyRxFrequencyDto[]>([]);
  // Store filtered stats per selected locations
  const [therapyRxFrequency, setTherapyRxFrequency] = useState<TherapyRxFrequencyData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!debounceStartDate || !debounceEndDate) {
        // console.log("[Therapy Rx Frequency] Waiting for user to select both dates...");
        return;
      }
      
      try {
        setIsLoading(true);

        const response = await getTherapyRxFrequencyStats(debounceStartDate, debounceEndDate);
        // console.log("[Therapy Rx Frequency] Backend responded with:", response);
        
        if (response && response.length > 0) {
          setAllTherapyRxFrequency(response);
        } else {
          // console.log("[Therapy Rx Frequency] No data found for the specified range.");
          setAllTherapyRxFrequency([]);
          setTherapyRxFrequency([]);
        }
      } catch (error) {
        // console.log("[Therapy Rx Frequency] Error fetching stats: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData(); 
  }, [debounceStartDate, debounceEndDate, getTherapyRxFrequencyStats]);

  // Filter locally whenever locations change and calculate frequency (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allTherapyRxFrequency.length > 0) {
      let filteredData = allTherapyRxFrequency;
      
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        filteredData = allTherapyRxFrequency.filter(p => memoizedLocationIds.includes(p.locationId));
      }
      
      const frequencyData = calculateTherapyRxFrequency(filteredData);
      setTherapyRxFrequency(frequencyData);
    }
  }, [memoizedLocationIds, allTherapyRxFrequency, isLoading]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allTherapyRxFrequency.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allTherapyRxFrequency]);

  return { therapyRxFrequency, isLoading, availableLocations };
};

export default useTherapyRxFrequencyStats;
